import { atom } from "jotai";
import { atomWithStorage, atomFamily } from "jotai/utils";
import type { Zone } from "../types";
import { fetchZoneById } from "../api/zones";
import { makeSessionJsonStorage, withPrefix } from "./storage";

// selected zone-ID:s 
export const selectedZoneIdsAtom = atom<string[]>([]);

// active zone 
export const activeZoneIdAtom = atomWithStorage<string | null>(
  withPrefix("active-zone-id"),
  null,
  makeSessionJsonStorage<string | null>() 
);

// Cache & helpers
export const zonesByIdAtom = atom(new Map<string, Zone>());
const zonesInflightAtom = atom(new Map<string, Promise<Zone | null>>());

export const upsertZonesAtom = atom(null, (get, set, zones: Zone[]) => {
  const prev = get(zonesByIdAtom);
  let changed = false;
  const next = new Map(prev);

  for (const z of zones) {
    const id = String(z.id);
    const prevZ = prev.get(id);
    if (prevZ === z) continue;
    if (prevZ && prevZ.polygon === z.polygon) continue;
    next.set(id, z);
    changed = true;
  }
  if (changed) set(zonesByIdAtom, next);
});

export const selectedZonesAtom = atom((get) => {
  const ids = get(selectedZoneIdsAtom);
  const map = get(zonesByIdAtom);
  return ids.map((id) => map.get(id)).filter((z): z is Zone => !!z);
});

  //  Preload with inflight-dedup
export const preloadZonesAtom = atom(null, async (get, set, ids: string[]) => {
  const cache = get(zonesByIdAtom);
  const inflight = new Map(get(zonesInflightAtom));

  const missing = ids.filter((id) => !cache.get(id));
  if (missing.length === 0) return;

  const promises = missing.map((id) => {
    const existing = inflight.get(id);
    if (existing) return existing;
    const p = fetchZoneById(id).catch(() => null);
    inflight.set(id, p);
    return p;
  });

  set(zonesInflightAtom, inflight);

  const results = await Promise.all(promises);

  const nextCache = new Map(cache);
  let changed = false;

  results.forEach((z) => {
    if (z && !nextCache.get(z.id)) {
      nextCache.set(z.id, z);
      changed = true;
    }
    if (z) inflight.delete(z.id);
  });

  if (changed) set(zonesByIdAtom, nextCache);
  set(zonesInflightAtom, inflight);
});

// by-zon selector 

export const zoneByIdAtomFamily = atomFamily((id: string) =>
  atom(async (get) => {
    const cached = get(zonesByIdAtom).get(id);
    if (cached) return cached;
    const z = await fetchZoneById(id);
    return z;
  })
);
